import { useEffect, useState } from "react";
import { supabase } from "../lib/SupabaseClient";
import { useAuth } from "../context/AuthContext";

interface Candidate {
    ApplicantId: string;
    FullName: string;
    Email: string;
    Phone: string;
    Pin: string;
    ExternalExamId: string;
    selected?: boolean;
    status?: "pending" | "success" | "error";
    errors?: string[];
}

interface Test {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    is_published: boolean;
}

export default function ImportCandidatesPage() {
    const { user } = useAuth(); // ✅ get operator info
    const [tests, setTests] = useState<Test[]>([]);
    const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(false);

    // ✅ Load active, unpublished tests
    useEffect(() => {
        async function loadTests() {
            const { data, error } = await supabase
                .from("tests")
                .select("id, name, description, is_active, is_published")
                .eq("is_active", true)
                .eq("is_published", false);

            if (error) {
                console.error("❌ Error loading tests:", error.message);
            } else {
                setTests(data || []);
            }
        }
        loadTests();
    }, []);

    // ✅ Handle file upload (JSON parsing)
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                let json = JSON.parse(event.target?.result as string);

                // Support both raw array and { Applicants: [...] }
                if (!Array.isArray(json) && json.Applicants) {
                    json = json.Applicants;
                }

                if (!Array.isArray(json)) {
                    throw new Error("JSON must be an array of candidates");
                }

                setCandidates(
                    json.map((c: Candidate) => ({
                        ...c,
                        selected: true,
                        status: "pending",
                        errors: [],
                    }))
                );
            } catch (err: any) {
                alert("❌ Invalid JSON file: " + err.message);
            }
        };
        reader.readAsText(file);
    };

    // ✅ Handle Import
    async function handleImport() {
        if (!selectedTestId) {
            alert("Please select a test first.");
            return;
        }

        const checked = candidates.filter((c) => c.selected);
        if (checked.length === 0) {
            alert("Please select at least one candidate to import.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(
                "https://rwnorjluqkcuqitkuuxw.functions.supabase.co/import-candidates",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        applicants: checked,
                        testId: selectedTestId,
                        assignedBy: user?.id || null, // ✅ operator id
                    }),
                }
            );

            const { results } = await res.json();

            // Update candidates with results
            setCandidates((prev) =>
                prev.map((c) => {
                    const r = results.find((r: any) => r.applicantId === c.ApplicantId);
                    return r ? { ...c, status: r.status, errors: r.errors } : c;
                })
            );
        } catch (err: any) {
            console.error("❌ Import failed", err);
            alert("Import failed: " + err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Import Candidates</h1>

            {/* Select Test */}
            <div className="mb-4">
                <label className="block mb-1 font-medium">Select Test</label>
                <select
                    className="w-full border rounded px-3 py-2"
                    value={selectedTestId || ""}
                    onChange={(e) => setSelectedTestId(Number(e.target.value))}
                >
                    <option value="">-- Select an active, not published test --</option>
                    {tests.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Upload JSON */}
            <div className="mb-4">
                <label className="block mb-1 font-medium">Upload JSON File</label>
                <input type="file" accept=".json" onChange={handleFileUpload} />
            </div>

            {/* Import Button */}
            <button
                className="bg-indigo-600 text-white px-4 py-2 rounded disabled:opacity-50"
                onClick={handleImport}
                disabled={loading || candidates.length === 0 || !selectedTestId}
            >
                {loading ? "Importing..." : "Import Selected"}
            </button>

            {/* Results Table */}
            {candidates.length > 0 && (
                <div className="mt-6 overflow-x-auto">
                    <table className="w-full border text-sm">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="p-2">✔</th>
                                <th className="p-2">ApplicantId</th>
                                <th className="p-2">Full Name</th>
                                <th className="p-2">Email</th>
                                <th className="p-2">Phone</th>
                                <th className="p-2">Status</th>
                                <th className="p-2">Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidates.map((c, i) => (
                                <tr key={i} className="border-t">
                                    <td className="p-2">
                                        <input
                                            type="checkbox"
                                            checked={c.selected ?? true}
                                            onChange={(e) =>
                                                setCandidates((prev) =>
                                                    prev.map((p, idx) =>
                                                        idx === i
                                                            ? { ...p, selected: e.target.checked }
                                                            : p
                                                    )
                                                )
                                            }
                                        />
                                    </td>
                                    <td className="p-2">{c.ApplicantId}</td>
                                    <td className="p-2">{c.FullName}</td>
                                    <td className="p-2">{c.Email}</td>
                                    <td className="p-2">{c.Phone}</td>
                                    <td className="p-2">
                                        {c.status === "success"
                                            ? "✅ Success"
                                            : c.status === "error"
                                                ? "❌ Error"
                                                : "⏳ Pending"}
                                    </td>
                                    <td className="p-2 text-red-600">
                                        {c.errors?.map((e, idx) => (
                                            <div key={idx}>{e}</div>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
